import { InfoCircle } from '@untitled-ui/icons-react';
import moment from 'moment';
import React, { useCallback, useMemo } from 'react';

import { Callout } from '@myhealth-raccoon/components';
import { useAsyncData } from '@myhealth-raccoon/services';

import { AvailabilityProps, Timeslot } from '.';
import TelehealthAfternoon from 'src/assets/telehealth-afternoon.png';
import TelehealthMorning from 'src/assets/telehealth-morning.png';
import { useClinicSelector } from 'src/hooks/useAsyncSelector';
import {
    AutomedDoctorAvailabilityTimeModel,
    AutomedDoctorAvailablilityModel,
    SiteBookingAvailabilityModel,
    availabilityService,
} from 'src/services/availability';
import { isSlotAfternoon } from 'src/utils/mapAutomedTimeTypes';

export function OptionButton({
    src,
    children,
    onClick,
    disabled,
}: {
    src: string;
    children: React.ReactNode;
    onClick?: React.MouseEventHandler<HTMLButtonElement>;
    disabled?: boolean;
}) {
    return (
        <button
            type="button"
            className="hover:ring-primary-300 flex flex-row items-center gap-2 rounded bg-white p-3 text-left text-sm font-normal ring-1 ring-gray-200 transition-colors disabled:pointer-events-none disabled:bg-gray-200 disabled:opacity-75"
            onClick={onClick}
            disabled={disabled}
        >
            <img src={src} className="size-8 flex-none rounded-full border border-black/5 text-gray-900" />
            <p className="">{children}</p>
        </button>
    );
}

const today = moment();

export function NonSpecificAvailability({
    doctorId,
    clinicId,
    appointmentType,
    onSelectTimeSlot,
    infoText,
}: AvailabilityProps) {
    const { data: availabilityData, ...timeslotsRequest } = useAsyncData(
        {
            service: availabilityService,
            method: availabilityService.GetBookingTimeslot,
            defaultValue: [],
            suppressToast: true,
            selector: useCallback(
                (data: SiteBookingAvailabilityModel[] | AutomedDoctorAvailablilityModel) => {
                    let obj = [] as Timeslot[];

                    if (Array.isArray(data)) {
                        obj = data.reduce((obj, { user, timeSlots }) => {
                            if (!timeSlots) {
                                return obj;
                            }
                            for (const [date, slots] of Object.entries(timeSlots)) {
                                if (date !== today.format('YYYY-MM-DD')) {
                                    continue;
                                }
                                for (const ts of slots) {
                                    const existingSlot = obj.find(({ time }) => time === ts.t);

                                    if (existingSlot) {
                                        existingSlot.slots.push({ userId: user.userId, duration: ts.d, ts });
                                    } else {
                                        obj.push({
                                            time: ts.t,
                                            slots: [{ userId: user.userId, duration: ts.d, ts }],
                                        });
                                        obj = obj.sort(({ time: a }, { time: b }) =>
                                            moment(a, 'HH:mm').isBefore(moment(b, 'HH:mm')) ? -1 : 1
                                        );
                                    }
                                }
                            }
                            return obj;
                        }, obj);
                    } else if (data.timeslot) {
                        for (const [date, slots] of Object.entries(data.timeslot)) {
                            if (date !== today.format('YYYY-MM-DD')) {
                                continue;
                            }
                            obj.push(
                                ...slots.map((ts) => ({
                                    time: ts.time,
                                    slots: [{ userId: Number(doctorId!), duration: ts.duration, ts }],
                                }))
                            );
                        }
                    }
                    return obj;
                },
                [doctorId]
            ),
        },
        clinicId,
        appointmentType.appointmentTypeId,
        doctorId,
        useMemo(
            () => ({
                startDate: today.toDate(),
                endDate: today.toDate(),
                firstAvailableOrCalendar: false,
            }),
            []
        )
    );

    const { data: clinic } = useClinicSelector();

    const [nextMorningSlot, nextAfternoonSlot] = useMemo(() => {
        const crossIndex = availabilityData.findIndex((ts) => isSlotAfternoon(ts, clinic!));
        const mornings = availabilityData.slice(0, crossIndex);
        const afternoons = availabilityData.slice(crossIndex);
        return [mornings[0]?.slots[0], afternoons[0]?.slots[0]];
    }, [availabilityData, clinic]);

    return (
        <>
            {!timeslotsRequest.isLoading && availabilityData.length == 0 ? (
                <div className="px-4">
                    <Callout color="error" className="font-medium">
                        There are currently no sessions available for this appointment type.
                    </Callout>
                </div>
            ) : (
                <>
                    {infoText && (
                        <Callout Icon={InfoCircle} color="info" className="mx-4">
                            {infoText}
                        </Callout>
                    )}
                    <div className="grid gap-4 p-4 sm:grid-cols-2 md:grid-cols-4">
                        {timeslotsRequest.isLoading ? (
                            <>
                                <div className="h-16 animate-pulse rounded bg-gray-100"></div>
                                <div className="h-16 animate-pulse rounded bg-gray-100"></div>
                            </>
                        ) : (
                            <>
                                <OptionButton
                                    src={TelehealthMorning}
                                    disabled={!nextMorningSlot}
                                    onClick={() =>
                                        onSelectTimeSlot({
                                            date: today.toDate(),
                                            doctorId: nextMorningSlot.userId,
                                            appointmentTypeId: appointmentType.appointmentTypeId,
                                            clinicId,
                                            timeSlot: nextMorningSlot.ts as AutomedDoctorAvailabilityTimeModel,
                                        })
                                    }
                                >
                                    {/* TODO: rendered time should be based on opening hours */}
                                    <strong className="font-semibold">Next Available GP - Morning</strong> (9am - 1pm)
                                </OptionButton>
                                <OptionButton
                                    src={TelehealthAfternoon}
                                    disabled={!nextAfternoonSlot}
                                    onClick={() =>
                                        onSelectTimeSlot({
                                            date: today.toDate(),
                                            doctorId: nextAfternoonSlot.userId,
                                            appointmentTypeId: appointmentType.appointmentTypeId,
                                            clinicId,
                                            timeSlot: nextAfternoonSlot.ts as AutomedDoctorAvailabilityTimeModel,
                                        })
                                    }
                                >
                                    {/* TODO: rendered time should be based on opening hours */}
                                    <strong className="font-semibold">Next Available GP - Afternoon</strong> (1pm - 6pm)
                                </OptionButton>
                            </>
                        )}
                    </div>
                </>
            )}
        </>
    );
}
